import { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { newRequestId } from './requestId';

const active = job => ['queued', 'running'].includes(job?.status);
const pause = (ms, signal) => new Promise((resolve, reject) => {
  const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
  const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, ms);
  signal.addEventListener('abort', abort, { once: true });
  if (signal.aborted) abort();
});
const readPointer = key => { try { return localStorage.getItem(key); } catch { return null; } };
const writePointer = (key, id) => { try { if (id) localStorage.setItem(key, id); else localStorage.removeItem(key); } catch { /* Server status remains authoritative. */ } };

export default function useGenerationJob(scope, onComplete) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reconnecting, setReconnecting] = useState(false);
  const callback = useRef(onComplete);
  callback.current = onComplete;
  const controller = useRef(null);
  const working = useRef(false);
  const key = scope ? `startin-generation:${scope}` : null;

  async function watch(id, signal, first) {
    let current = first, failures = 0;
    const deadline = Date.now() + 30 * 60 * 1000;
    while (!signal.aborted && Date.now() < deadline) {
      try {
        if (!current) current = (await api.get(`/reports/jobs/${id}`, { signal, timeout: 15000 })).data;
        if (signal.aborted) return;
        if (!['queued', 'running', 'completed', 'failed', 'interrupted'].includes(current.status)) throw new Error('Invalid generation status');
        setJob(current); setBusy(active(current) || current.status === 'completed'); setReconnecting(false); failures = 0;
        if (current.status === 'completed') {
          const { data } = await api.get(`/assessments/${current.result_assessment_id}`, { signal, timeout: 15000 });
          if (signal.aborted) return;
          writePointer(key, null);
          setBusy(false);
          callback.current({ ...data, assessment_id: data.id, reports: data.reports }, current);
          return;
        }
        if (!active(current)) { setError(current.error || 'Generation was interrupted. Please retry.'); return; }
        await pause(2000, signal); current = null;
      } catch (e) {
        if (signal.aborted) return;
        if ([401, 403, 404].includes(e.response?.status)) {
          setBusy(false);
          if (e.response.status === 404) writePointer(key, null);
          setError(e.response.status === 404 ? 'This generation request was not found. You can start again.' : 'Please sign in again to check your generation.');
          return;
        }
        setReconnecting(true);
        try { await pause(Math.min(10000, 1000 * 2 ** Math.min(++failures, 4)), signal); }
        catch { if (signal.aborted) return; }
        current = null;
      }
    }
    if (!signal.aborted) { setError('Generation may still be running. Reconnect to check its status.'); setReconnecting(true); }
  }

  function begin() {
    controller.current?.abort();
    controller.current = new AbortController();
    setError(''); setReconnecting(false); setBusy(true); working.current = true;
    return controller.current.signal;
  }
  async function connect(id) {
    const signal = begin();
    try { await watch(id, signal); } catch (e) { /* Unmount aborts polling, not server generation. */ }
    finally { if (!signal.aborted) working.current = false; }
  }
  useEffect(() => {
    setJob(null); setError(''); setBusy(false); working.current = false;
    const id = key && readPointer(key);
    if (id) connect(id);
    return () => controller.current?.abort();
    // Polling lifetime follows the signed-in user and assessment, not renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  async function start(payload) {
    if (!key || working.current) return;
    const signal = begin();
    const id = newRequestId();
    writePointer(key, id);
    try {
      // A retry after a lost POST response must keep the SAME UUID.
      let response;
      try { response = await api.post('/reports/jobs', { ...payload, request_id: id }, { signal, timeout: 30000 }); }
      catch (e) {
        if (signal.aborted || e.response) throw e;
        response = await api.post('/reports/jobs', { ...payload, request_id: id }, { signal, timeout: 30000 });
      }
      writePointer(key, response.data.id);
      await watch(response.data.id, signal, response.data);
    } catch (e) {
      if (signal.aborted) return;
      if (e.response) { writePointer(key, null); setBusy(false); setError(typeof e.response.data?.detail === 'string' ? e.response.data.detail : 'Generation could not be started.'); }
      else { setReconnecting(true); await watch(id, signal); }
    } finally { if (!signal.aborted) working.current = false; }
  }
  async function retry() {
    if (!job?.can_retry || working.current) return;
    const signal = begin();
    try {
      const { data } = await api.post(`/reports/jobs/${job.id}/retry`, {}, { signal, timeout: 30000 });
      writePointer(key, data.id);
      await watch(data.id, signal, data);
    } catch (e) {
      if (!signal.aborted) { setBusy(false); setError(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Retry could not be confirmed. Reconnect to check the saved generation.'); setReconnecting(!e.response); }
    } finally { if (!signal.aborted) working.current = false; }
  }
  return { job, busy, error, reconnecting, start, retry,
    reconnect: () => { const id = key && readPointer(key); if (id) connect(id); } };
}
